import {
  deleteTicketDependentIncorporation,
  getTicketDependentIncorporationCoverages,
  getTicketDependentIncorporations,
  getTicketAvailableCoverages,
  getTicketAvailableCoverageIncorporations,
  patchTicketStatus,
  patchTicketDependentIncorporation,
  postTicketDependentIncorporation,
  putTicketDependentIncorporationCoverages
} from '../../../network';

import {
  FETCH_TICKET_DEPENDANTS_INCORPORATIONS,
  SET_TICKET_DEPENDANTS_INCORPORATIONS_LOADING,
  SET_TICKET_DEPENDANTS_INCORPORATIONS,
  GET_TICKET_DEPENDANTS_INCORPORATIONS,
  DELETE_TICKET_DEPENDANT_INCORPORATION,
  FETCH_TICKET_AVAILABLE_COVERAGES,
  FETCH_TICKET_AVAILABLE_COVERAGE_INCORPORATIONS,
  FETCH_TICKET_DEPENDENT_INCORPORATIONS,
  SET_TICKET_AVAILABLE_COVERAGES,
  GET_TICKET_AVAILABLE_COVERAGES,
  POST_TICKET_DEPENDENT_INCORPORATION,
  PATCH_TICKET_DEPENDENT_INCORPORATION,
  DELETE_TICKET_DEPENDENT_INCORPORATION,
  FETCH_TICKET_DEPENDENT_INCORPORATION_COVERAGES,
  PUT_TICKET_DEPENDENT_INCORPORATION_COVERAGES,
  PATCH_TICKET_STATUS
} from '@/store/types';

const store = {};
const getDefaultState = () => ({
  dependants: {
    data: [],
    loading: false
  },
  availableCoverages: []
});

const resolveTicketScope = (payload) => {
  if (typeof payload === 'object' && payload !== null) {
    return {
      ticketId: payload.ticketId ?? payload.id,
      token: payload.token
    };
  }

  return {
    ticketId: payload,
    token: undefined
  };
};

store.state = getDefaultState();

store.getters = {
  [GET_TICKET_DEPENDANTS_INCORPORATIONS](state) {
    return state.dependants;
  },
  [GET_TICKET_AVAILABLE_COVERAGES](state) {
    return state.availableCoverages;
  }
};

store.mutations = {
  [SET_TICKET_DEPENDANTS_INCORPORATIONS_LOADING](state, loading) {
    state.dependants.loading = loading;
  },
  [SET_TICKET_DEPENDANTS_INCORPORATIONS](state, dependants) {
    state.dependants.data = dependants;
  },
  [SET_TICKET_AVAILABLE_COVERAGES](state, availableCoverages) {
    state.availableCoverages = availableCoverages;
  }
};

store.actions = {
  async [FETCH_TICKET_DEPENDANTS_INCORPORATIONS]({ commit }, id) {
    const { ticketId, token } = resolveTicketScope(id);
    commit(SET_TICKET_DEPENDANTS_INCORPORATIONS_LOADING, true);
    commit(SET_TICKET_DEPENDANTS_INCORPORATIONS, []);
    try {
      const response = await getTicketDependentIncorporations(ticketId, token);
      commit(SET_TICKET_DEPENDANTS_INCORPORATIONS, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching ticket dependants', error);
      console.error('Params:', ticketId);
      throw error;
    } finally {
      commit(SET_TICKET_DEPENDANTS_INCORPORATIONS_LOADING, false);
    }
  },
  async [DELETE_TICKET_DEPENDANT_INCORPORATION](
    { commit, dispatch },
    { id, ticketId, token }
  ) {
    commit(SET_TICKET_DEPENDANTS_INCORPORATIONS_LOADING, true);
    try {
      const response = await deleteTicketDependentIncorporation(
        ticketId,
        id,
        token
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting ticket dependant', error);
      console.error('Params:', id);
      throw error;
    } finally {
      commit(SET_TICKET_DEPENDANTS_INCORPORATIONS_LOADING, false);
      dispatch(FETCH_TICKET_DEPENDANTS_INCORPORATIONS, ticketId);
    }
  },
  async [FETCH_TICKET_AVAILABLE_COVERAGES]({ commit }, payload) {
    const { ticketId, token } = resolveTicketScope(payload);
    const response = await getTicketAvailableCoverages(ticketId, token);
    const normalizedCoverages = (response.data || []).map((coverage) => ({
      ...coverage,
      coverageGroupCompanyId: coverage.coverageGroupCompanyId ?? coverage.id,
      employeeCoverageId: coverage.employeeCoverageId || coverage.id,
      operationalId:
        coverage.operationalId || coverage.employeeCoverageId || coverage.id
    }));
    commit(SET_TICKET_AVAILABLE_COVERAGES, normalizedCoverages);
    return normalizedCoverages;
  },
  async [FETCH_TICKET_AVAILABLE_COVERAGE_INCORPORATIONS]({ commit }, payload) {
    const { ticketId, token } = resolveTicketScope(payload);
    const response = await getTicketAvailableCoverageIncorporations(
      ticketId,
      token
    );
    const normalizedCoverages = (response.data || []).map((coverage) => ({
      ...coverage,
      coverageGroupCompanyId: coverage.coverageGroupCompanyId ?? coverage.id,
      employeeCoverageIncorporationId:
        coverage.employeeCoverageIncorporationId ?? coverage.id,
      operationalId: coverage.employeeCoverageIncorporationId || coverage.id
    }));
    commit(SET_TICKET_AVAILABLE_COVERAGES, normalizedCoverages);
    return normalizedCoverages;
  },
  async [FETCH_TICKET_DEPENDENT_INCORPORATIONS](_, payload) {
    const { ticketId, token } = resolveTicketScope(payload);
    const response = await getTicketDependentIncorporations(ticketId, token);
    return response.data;
  },
  async [POST_TICKET_DEPENDENT_INCORPORATION](_, { ticketId, payload, token }) {
    const response = await postTicketDependentIncorporation(
      ticketId,
      payload,
      token
    );
    return response.data;
  },
  async [PATCH_TICKET_DEPENDENT_INCORPORATION](
    _,
    { ticketId, dependentIncorporationId, payload, token }
  ) {
    const response = await patchTicketDependentIncorporation(
      ticketId,
      dependentIncorporationId,
      payload,
      token
    );
    return response.data;
  },
  async [DELETE_TICKET_DEPENDENT_INCORPORATION](
    _,
    { ticketId, dependentIncorporationId, token }
  ) {
    const response = await deleteTicketDependentIncorporation(
      ticketId,
      dependentIncorporationId,
      token
    );
    return response.data;
  },
  async [FETCH_TICKET_DEPENDENT_INCORPORATION_COVERAGES](
    _,
    { ticketId, dependentIncorporationId, token }
  ) {
    const response = await getTicketDependentIncorporationCoverages(
      ticketId,
      dependentIncorporationId,
      token
    );
    return response.data;
  },
  async [PUT_TICKET_DEPENDENT_INCORPORATION_COVERAGES](
    _,
    { ticketId, dependentIncorporationId, payload, token }
  ) {
    const response = await putTicketDependentIncorporationCoverages(
      ticketId,
      dependentIncorporationId,
      payload,
      token
    );
    return response.data;
  },
  async [PATCH_TICKET_STATUS](_, { ticketId, status = 7, token }) {
    const response = await patchTicketStatus(ticketId, { status }, token);
    return response.data;
  }
};

export default store;
